import { UserProfile } from '@/shared/types';
import { authService } from '@/shared/services/realData/auth';

/**
 * Subscription operations
 * Handles all subscription-related operations
 */
export const subscriptionService = {
  /**
   * Calculate plan price based on plan ID and billing cycle
   */
  calculatePlanPrice(planId: string, billingCycle: string, customMonths?: number): number {
    // Subscription prices are always in USD regardless of user's country
    const prices: Record<string, Record<string, number>> = {
      free: { monthly: 0, yearly: 0, quarterly: 0, custom: 0 },
      basic: { monthly: 1, yearly: 10, quarterly: 2.5, custom: 0.1 }, // $1/month, $10/year
      pro: { monthly: 10, yearly: 100, quarterly: 25, custom: 1 }, // $10/month, $100/year
      enterprise: { monthly: 29, yearly: 290, quarterly: 72.5, custom: 2.9 }
    };
    const basePrice = prices[planId]?.[billingCycle] || 0;

    // For custom billing, multiply by months
    if (billingCycle === 'custom') {
      const months = customMonths || 12;
      const monthlyRate = prices[planId]?.monthly || 0;
      return monthlyRate * months;
    }

    return basePrice;
  },

  /**
   * Record subscription payment
   */
  async recordSubscriptionPayment(
    amount: number,
    planId: string,
    paymentMethod: string,
    userId?: string,
    billingPeriod: 'monthly' | 'quarterly' | 'yearly' | 'custom' = 'monthly',
    customMonths: number = 1
  ): Promise<void> {
    const user = authService.getProfile();
    const targetUserId = userId || user?.id;

    if (!targetUserId) throw new Error('User not authenticated');

    console.log('Recording subscription payment:', {
      amount,
      planId,
      paymentMethod,
      userId: targetUserId,
      billingPeriod,
      customMonths
    });

    // Implementation would go here - save to database
    // For now, just log the payment
  },

  /**
   * Get days remaining in subscription
   */
  getSubscriptionDaysRemaining(profile: UserProfile): number {
    if (!profile.subscription?.nextBillingDate) return 0;
    const nextBilling = new Date(profile.subscription.nextBillingDate);
    const now = new Date();
    const diffTime = nextBilling.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  },

  /**
   * Get transactions for a user
   * Uses Supabase client directly for client-side queries
   */
  async getTransactions(userId?: string): Promise<any[]> {
    try {
      // Import SupabaseService dynamically to avoid server-side dependency
      const { supabaseService } = await import('@/shared/services/supabaseService');

      // Get current user if userId not provided
      const targetUserId = userId || await authService.getCurrentUserSupabaseId();
      if (!targetUserId) return [];

      // Use Supabase client to get invoices
      const { data: invoices, error } = await supabaseService.client
        .from('invoices')
        .select('*')
        .eq('user_id', targetUserId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching transactions:', error);
        return [];
      }

      if (!invoices || invoices.length === 0) {
        return [];
      }

      // Transform invoice data into transaction format
      // Note: Supabase returns snake_case column names
      const transactions = invoices.map((invoice: any) => {
        const totalAmount = typeof invoice.total_amount === 'number' ? invoice.total_amount :
                           parseFloat(invoice.total_amount) || 0;
        const paidAmount = typeof invoice.paid_amount === 'number' ? invoice.paid_amount :
                          parseFloat(invoice.paid_amount) || 0;
        const invoiceNumber = invoice.invoice_number || invoice.id?.substring(0, 8).toUpperCase();

        return {
          id: invoice.id,
          date: invoice.created_at,
          amount: totalAmount,
          amountPaid: paidAmount,
          currency: invoice.currency || 'USD',
          status: totalAmount <= paidAmount ? 'completed' :
                  new Date(invoice.due_date) < new Date() ? 'failed' : 'pending',
          type: 'invoice',
          description: `Invoice #${invoiceNumber}`,
          paypalTransactionId: invoice.paypal_transaction_id,
          paypalSubscriptionId: invoice.paypal_subscription_id,
          planId: invoice.plan_id,
          billingPeriod: invoice.billing_period,
          nextBillingDate: invoice.next_billing_date
        };
      });

      return transactions;
    } catch (error) {
      console.error('Error getting transactions:', error);
      return [];
    }
  }
};
